import axios from "axios";
import config from '../config';
import { StripeAccountSessionComponentsType } from "../data/types";

const new_api_host = config.apiUrl;
const secretKey = process.env.REACT_APP_SECRET_KEY ? process.env.REACT_APP_SECRET_KEY : ""
const apiKey = process.env.REACT_APP_API_KEY ? process.env.REACT_APP_API_KEY : ""


const axiosInstance = axios.create({
  baseURL: new_api_host,
  timeout: 100000,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

axiosInstance.defaults.headers.common['HOMECOOK-SECRET-KEY'] = secretKey
axiosInstance.defaults.headers.common['HOMECOOK-API-KEY'] = apiKey


// Stripe Connect API endpoints

export type CreatePaymentAccountSchema = {
  communityId?: string;
  userId?: string;
  type: 'express' | 'standard';
  country: string;
  email: string;
  capabilities: {
    card_payments: { requested: boolean };
    transfers: { requested: boolean };
  };
}

// Create a connected account
export const createConnectedAccount = async (data: CreatePaymentAccountSchema) => {
    if (!data.communityId || !data.userId) {
        console.error('Community ID and User ID are required to create connected account');
        throw new Error('Community ID and User ID are required to create connected account');
        // return null;
    }

  try {
    const response = await axiosInstance.post('/stripe/create-account', data);
    return response.data;
  } catch (error) {
    console.error('Error creating connected account:', error);
    throw error;
    // return null;
  }
};

// Get connected account
export const getConnectedAccount = async (communityId: string, userId: string) => {
    if (!communityId || !userId) {
        console.error('Community ID and User ID are required to get connected account');
        throw new Error('Community ID and User ID are required to get connected account');
        // return null;
    }

    try {
        // Use axios params to ensure query params are encoded correctly
        const response = await axiosInstance.get('/stripe/get-account', { params: { communityId, userId } });

        // --- Normalise response shape --------------------------------------------------
        const body: any = response.data;
        // Some back-ends wrap payload in a `data` field – honour that first.
        const payload = body?.data ?? body;

        // Helper to try multiple field names
        const pick = (obj: any, ...keys: string[]) => {
            for (const k of keys) {
                if (obj && Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
            }
            return undefined;
        };

        // Determine the connected account identifier (striping variants)
        const accountId = pick(payload, 'accountId', 'account_id', 'id');
        // Determine the Stripe account object payload
        const accountData = pick(payload, 'accountData', 'account') ?? payload;

        // If no account was found return empty so callers fall back to default view
        if (!accountId) return { accountId: '', accountData: null };

        return { accountId, accountData };
    } catch (error) {
        console.error('Error getting connected account:', error);
        throw error;
        // return null;
    }
};

// Create onboarding link
export const createOnboardingLink = async (data: {
  accountId: string;
  returnUrl: string;
  refreshUrl: string;
}) => {
    if (!data.accountId || !data.returnUrl || !data.refreshUrl) {
        console.error('Account ID, Return URL, and Refresh URL are required to create onboarding link');
        throw new Error('Account ID, Return URL, and Refresh URL are required to create onboarding link');
        // return null;
    }

  try {
    const response = await axiosInstance.post('/stripe/create-onboarding-link', data);
    return response.data;
  } catch (error) {
    console.error('Error creating onboarding link:', error);
    throw error;
    // return null;
  }
};

// Create dashboard link
export const createDashboardLink = async (data: {
  accountId: string;
  returnUrl: string;
  refreshUrl: string;
}) => {
    if (!data.accountId || !data.returnUrl || !data.refreshUrl) {
        console.error('Account ID and Return URL are required to create dashboard link');
        throw new Error('Account ID and Return URL are required to create dashboard link');
        // return null;
    }

    try {
        const response = await axiosInstance.post('/stripe/create-dashboard-link', data);
        return response.data;
    } catch (error) {
        console.error('Error creating dashboard link:', error);
        throw error;
        // return null;
    }
};


// Create AccountSession for embedded components
export const createAccountSession = async (data: {
  accountId: string;
  components?: StripeAccountSessionComponentsType;
}) => {
    // console.log("createAccountSession", data)
    if (!data.accountId) {
        console.error('Account ID is required to create account session');
        throw new Error('Account ID is required to create account session');
    }

    try {
        // Check if we're using private preview components (like reporting_chart)
        const hasPreviewComponents = data.components?.reporting_chart?.enabled;
        
        // Create headers for the request
        const headers: any = {};
        
        // Add beta header for private preview components
        if (hasPreviewComponents) {
            headers['embedded_connect_beta'] = 'v2';
        }

        const response = await axiosInstance.post('/stripe/create-account-session', data, { headers });
        // console.log("createAccountSession response", response.data)
        return response.data;
    } catch (error) {
        console.error('Error creating account session:', error);
        throw error;
    }
};

// Create payment intent
export const createPaymentIntent = async (data: {
  amount: number;
  currency: string;
  connectedAccountId: string;
  applicationFeeAmount: number;
}) => {
    if (!data.amount || !data.currency || !data.connectedAccountId || !data.applicationFeeAmount) {
        console.error('Amount, Currency, Connected Account ID, and Application Fee Amount are required to create payment intent');
        throw new Error('Amount, Currency, Connected Account ID, and Application Fee Amount are required to create payment intent');
        // return null;
    }

  try {
    const response = await axiosInstance.post('/stripe/create-payment-intent', data);
    return response.data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
    // return null;
  }
};

// Get payment history
export const getPaymentHistory = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/payment-history?accountId=${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting payment history:', error);
    throw error;
  }
};

// Create payout
export const createPayout = async (data: {
  accountId: string;
  amount: number;
  currency: string;
}) => {
  try {
    const response = await axiosInstance.post('/stripe/create-payout', data);
    return response.data;
  } catch (error) {
    console.error('Error creating payout:', error);
    throw error;
  }
};

// Get account balance
export const getAccountBalance = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/account-balance?accountId=${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting account balance:', error);
    throw error;
  }
};

// Update connected account
export const updateConnectedAccount = async (accountId: string, updates: any) => {
  try {
    const response = await axiosInstance.put(`/stripe/update-account/${accountId}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating connected account:', error);
    throw error;
  }
};

// Delete connected account
export const deleteConnectedAccount = async (accountId: string) => {
  try {
    const response = await axiosInstance.delete(`/stripe/delete-account/${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting connected account:', error);
    throw error;
  }
};

// Get account requirements
export const getAccountRequirements = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/account-requirements/${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting account requirements:', error);
    throw error;
  }
};

// Submit account requirements
export const submitAccountRequirements = async (accountId: string, requirements: any) => {
  try {
    const response = await axiosInstance.post(`/stripe/submit-requirements/${accountId}`, requirements);
    return response.data;
  } catch (error) {
    console.error('Error submitting account requirements:', error);
    throw error;
  }
};

// Get platform fees
export const getPlatformFees = async (communityId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/platform-fees/${communityId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting platform fees:', error);
    throw error;
  }
};

// Update platform fees
export const updatePlatformFees = async (communityId: string, fees: any) => {
  try {
    const response = await axiosInstance.put(`/stripe/platform-fees/${communityId}`, fees);
    return response.data;
  } catch (error) {
    console.error('Error updating platform fees:', error);
    throw error;
  }
};

// Get transfer history
export const getTransferHistory = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/transfer-history?accountId=${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting transfer history:', error);
    throw error;
  }
};

// Create transfer
export const createTransfer = async (data: {
  accountId: string;
  amount: number;
  currency: string;
  destination: string;
}) => {
  try {
    const response = await axiosInstance.post('/stripe/create-transfer', data);
    return response.data;
  } catch (error) {
    console.error('Error creating transfer:', error);
    throw error;
  }
};

// Webhook handlers for Stripe events
export const handleStripeWebhook = async (event: any) => {
  try {
    const response = await axiosInstance.post('/stripe/webhook', event);
    return response.data;
  } catch (error) {
    console.error('Error handling Stripe webhook:', error);
    throw error;
  }
};

// Get account verification status
export const getAccountVerificationStatus = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/verification-status/${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting verification status:', error);
    throw error;
  }
};

// Request account verification
export const requestAccountVerification = async (accountId: string, verificationData: any) => {
  try {
    const response = await axiosInstance.post(`/stripe/request-verification/${accountId}`, verificationData);
    return response.data;
  } catch (error) {
    console.error('Error requesting verification:', error);
    throw error;
  }
};

// Get account disputes
export const getAccountDisputes = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/disputes?accountId=${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting disputes:', error);
    throw error;
  }
};

// Respond to dispute
export const respondToDispute = async (disputeId: string, response: any) => {
  try {
    const apiResponse = await axiosInstance.post(`/stripe/disputes/${disputeId}/respond`, response);
    return apiResponse.data;
  } catch (error) {
    console.error('Error responding to dispute:', error);
    throw error;
  }
};

// Get account refunds
export const getAccountRefunds = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/refunds?accountId=${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting refunds:', error);
    throw error;
  }
};

// Create refund
export const createRefund = async (data: {
  paymentIntentId: string;
  amount?: number;
  reason?: string;
}) => {
  try {
    const response = await axiosInstance.post('/stripe/create-refund', data);
    return response.data;
  } catch (error) {
    console.error('Error creating refund:', error);
    throw error;
  }
};

// Get account analytics
export const getAccountAnalytics = async (accountId: string, period: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/analytics?accountId=${accountId}&period=${period}`);
    return response.data;
  } catch (error) {
    console.error('Error getting analytics:', error);
    throw error;
  }
};

// Get account settings
export const getAccountSettings = async (accountId: string) => {
  try {
    const response = await axiosInstance.get(`/stripe/settings/${accountId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting account settings:', error);
    throw error;
  }
};

// Update account settings
export const updateAccountSettings = async (accountId: string, settings: any) => {
  try {
    const response = await axiosInstance.put(`/stripe/settings/${accountId}`, settings);
    return response.data;
  } catch (error) {
    console.error('Error updating account settings:', error);
    throw error;
  }
}; 